
import { MessageSchemesIcons, NoseurObject, Scheme, Toaster } from "@rivtn/noseur"
import { SetURLSearchParams } from "react-router-dom";
import ErrorSuccessView from "../components/ErrorSuccessView";
import { i18nManager } from "../i18n";
import { ITransaction } from "../services";

export enum ActionType {

    VALIDATE_OTP = "VALIDATE_OTP",
    SEND_CARD_DETAIL = "SEND_CARD_DETAIL",

}

export enum FormStep {

    CARDINAL = "CARDINAL",
    THREE_DS = "THREE_DS",
    ENTER_PIN = "ENTER_PIN",
    ENTER_OTP = "ENTER_OTP",
    SELECT_BANK = "SELECT_BANK",
    SELECT_WALLET = "SELECT_WALLET",
    ENTER_OTP_3DS = "ENTER_OTP_3DS",
    CONFIRM_PAYMENT = "CONFIRM_PAYMENT",
    ENTER_CARD_DETAIL = "ENTER_CARD_DETAIL",
    
}

export type URLSearchParamsValue = string | string[] | number | number[] | undefined | null;

export const Utils = {

    AMOUNT_UNIT: 1000000,

    BUTTON_LOADING_PROPS: {
        disabled: true,
        rightIcon: "fas fa-spinner fa-pulse"
    },

    BUTTON_LOADING_PROPS_ICON_ONLY: {
        text: "",
        disabled: true,
        rightIcon: "fas fa-spinner fa-pulse"
    },

    SCREEN_DIMENSION: {
        width: (window.innerWidth > 0) ? window.innerWidth : window.screen?.width,
        height: (window.innerHeight > 0) ? window.innerHeight : window.screen?.height,
    },

    // composed

    buildPaymentCompletedView(transaction: ITransaction, serviceSuccesses: string[]) {
        return (<ErrorSuccessView transaction={transaction} redirectUrl={transaction?.redirects?.success} messages={serviceSuccesses} type="SUCCESS" actions={[
            {
                raised: true,
                scheme: Schemes.RIVTN_PLOUTOUS,
                text: i18nManager.Labels.common.back_to_business,
                onClick: () => {
                    window.location.replace(transaction.redirects.success);
                },
            }
        ]} />);
    },

    buildPartialPaymentView(transaction: ITransaction, serviceWarnings: string[], onCompletePayment: () => void, onConfirmPaymentAgain: () => void) {
        return (<ErrorSuccessView type="WARNING" transaction={transaction} messages={serviceWarnings} actions={[
            {
                raised: true,
                scheme: Schemes.RIVTN_PLOUTOUS,
                text: i18nManager.Labels.common.complete_payment,
                onClick: onCompletePayment,
            },
            {
                raised: true,
                scheme: Scheme.WARNING,
                text: i18nManager.Labels.common.confirm_payment_again,
                onClick: onConfirmPaymentAgain,
            },
            {
                raised: true,
                outlined: true,
                fillOnHover: true,
                scheme: Schemes.RIVTN_PLOUTOUS,
                text: i18nManager.Labels.common.back_to_business,
                onClick: () => {
                    window.location.replace(transaction.redirects.partial);
                },
            },
        ]} />);
    },

    buildPaymentErrorView(transaction: ITransaction, serviceErrors: string[], onTryAgain: () => void) {
        return (<ErrorSuccessView transaction={transaction} messages={serviceErrors} actions={[
            {
                raised: true,
                scheme: Scheme.DANGER,
                text: i18nManager.Labels.common.try_again,
                onClick: onTryAgain,
            },
            {
                outlined: true,
                scheme: Scheme.DARK,
                text: i18nManager.Labels.common.cancel,
                onClick: () => window.history.back(),
            }
        ]} />);
    },

    // end composed

    randomNumber(min: number = 1, max: number = 1000) {
        return Math.floor(Math.random() * (max - min + 1)) + min;
    },

    updateSearchParams(key: string, value: URLSearchParamsValue, setSearchParams: SetURLSearchParams | URLSearchParams, cb?: Function | undefined) {
        const fun = (prev: URLSearchParams) => {
            if (value === undefined || value === null) {
                prev.delete(key);
            } else if (Array.isArray(value)) {
                prev.delete(key);
                for (const v of value) prev.append(key, `${v}`);
            } else {
                prev.set(key, `${value}`);
            }
            return prev;
        };
        if (setSearchParams instanceof URLSearchParams) fun(setSearchParams);
        else setSearchParams(fun);
        cb && cb();
    },

    normalizeUrlParams(urlSearchParams: URLSearchParams, keyLookupMap: NoseurObject<string> = {}, extraParams: NoseurObject<string | number> = {}, reverseKeyLookupMap: NoseurObject<string> = {}) {
        const params: NoseurObject<any> = {};
        Object.keys(Object.fromEntries(urlSearchParams)).forEach((key: string) => {
            const lookupKey = keyLookupMap[key] ?? key;
            const values = urlSearchParams.getAll(reverseKeyLookupMap[lookupKey] ? lookupKey : key);
            if (values.length) {
                params[reverseKeyLookupMap[lookupKey] ?? lookupKey] = values.length > 1 ? values : values[0];
            }
        });
        Object.keys(extraParams).forEach((key) => params[key] = extraParams[key])
        return params;
    },

    urlParamsToSearch(urlSearchParams: URLSearchParams, only?: string[], includes?: NoseurObject<URLSearchParamsValue>) {
        only && only.length && Object.keys(Object.fromEntries(urlSearchParams)).forEach((key: string) => {
            if (!only.includes(key)) urlSearchParams.delete(key);
        });
        if (includes) {
            Object.keys(includes).forEach((key: string) => Utils.updateSearchParams(key, includes[key], urlSearchParams));
        }
        return urlSearchParams.toString();
    },

    extractDateDifferences(date: Date) {
        let seconds = Math.floor((date.getTime() - (new Date()).getTime())/ Utils.AMOUNT_UNIT);
        let minutes = Math.floor(seconds / 60);
        let hours = Math.floor(minutes / 60);
        let days = Math.floor(hours / 24);

        hours = hours - (days * 24);
        minutes = minutes - (days * 24 * 60) - (hours * 60);
        seconds = seconds - (days * 24 * 60 * 60) - (hours * 60 * 60) - (minutes * 60);
        return {
            days,
            hours,
            seconds,
            minutes,
        };
    },

    startsWith(value: string, cmp: string) {
        if (!cmp.length || !value.length) return 0;
        const limit = Math.min(value.length, cmp.length);
        for (let index = 0; index < limit; index++) {
            if (value[index] !== cmp[index]) return false;
        }
        return true;
    },

    inRange(_value: string, from: number, to: number = 999999) {
        const fromLimit = Math.min(`${from}`.length, _value.length)
        const value = parseInt(_value.substring(0, fromLimit));
        return value >= from && value <= to;
    },

    copyToClipboard(text: string, cb: (err?: any) => void) {
        if (navigator.clipboard) {
            navigator.clipboard.writeText(text).then(() => cb(), (err: any) => cb(err));
            return;
        }
        if ((window as any).clipboardData && (window as any).clipboardData.setData) {
            return (window as any).clipboardData.setData("Text", text);

        } else if (document.queryCommandSupported && document.queryCommandSupported("copy")) {
            var textarea = document.createElement("textarea");
            textarea.textContent = text;
            textarea.style.position = "fixed";
            document.body.appendChild(textarea);
            textarea.select();
            try {
                document.execCommand("copy");
                cb();
            } catch (ex) {
                cb(ex);
                return prompt("Copy to clipboard: Ctrl+C, Enter", text);
            } finally {
                document.body.removeChild(textarea);
            }
        }
    },

    sanitizeMobileNumber(msisdn: string, dialCode: string = "+234", appendDialCode?: boolean) {
        if (msisdn.startsWith(dialCode)) msisdn = msisdn.substring(dialCode.length);
        if (msisdn.startsWith(dialCode.replace("+", ""))) msisdn = msisdn.substring(dialCode.length - 1);
        if (appendDialCode) {
            if (msisdn.startsWith("0")) msisdn = msisdn.substring(1);
            msisdn = (dialCode + msisdn);
        }
        if (msisdn.startsWith("+")) msisdn = msisdn.substring(1);
        return msisdn;
    },

    collapseString: (value: string, max: number, contd: string = "...") => {
        let collapsedBack = "";
        let collapsedFront = "";
        let halfMax = Math.trunc(max / 2);
        if (!value || value?.length < max) return value;
        for (let index = 0; index < halfMax; index++) {
            collapsedFront += value[index];
            collapsedBack += value[value?.length - halfMax + index];
        }
        return collapsedFront + contd + collapsedBack;
    },

    toast: (content: any, scheme: Scheme) => {
        Toaster.toast({
            scheme,
            content,
            lifetime: 10000,
            foreScheme: true,
            showProgressbar: true,
            pauseDelayOnHover: true,
            style: { background: "white" },
            icon: MessageSchemesIcons.INFO,
        });
    },

    toastError: (content: any) => {
        Utils.toast(content, Scheme.DANGER);
    },

    toastSuccess: (content: any) => {
        Utils.toast(content, Schemes.RIVTN_PLOUTOUS);
    },

    equalsAny<T>(value: T, options: T[]) {
        for (const option of options) {
            if (option === value) return true;
        }
        return false;
    },

    paymentFullyPaid(status: number) {
        return (status >= 110 && status <= 117) || status >= 130;
    }

};

export * from "./Encryptor";
export type { Encryptor } from "./Encryptor";
export { CacheManager } from "./CacheManager";
export const Schemes: {
    RIVTN_PLOUTOUS: Scheme;
} = {
    RIVTN_PLOUTOUS: "rivtn-ploutous" as Scheme
}
